import React, { useState, useEffect } from "react";
import { Grid, Paper, Select } from "@material-ui/core";
import {
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { Formik, Form, Field, FieldArray } from "formik";
import clsx from "clsx";
import styled from "@emotion/styled";
import { useSnackbar } from "notistack";

import { getFacilities } from "../../../service/endpoint-manager";
import ButtonTextLoading from "../Shared/ButtonTextLoading";
import NarcoticsQualitativeQuestions from "./NarcoticsQualitativeQuestions";
import { narcoticsValidationSchema } from "../../../utils/narcoticsValidationSchema";

import { createRecordNarcotics } from "../../../service/endpoint-manager";

import styles from "../../../styles.module.css";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

const toggleOptions = [
  { value: "met", label: "Met" },
  { value: "unmet", label: "Unmet" },
];

const MuiToggleButton = styled(ToggleButton)(() => ({
  minWidth: "80px !important",
  height: "34px",
  borderRadius: "0px !important",
  // border: "2px solid white",
  // padding: "10px",
  color: "#415970",
  "&:first-child": {
    borderTopLeftRadius: "6px !important",
    borderBottomLeftRadius: "6px !important",
  },
  "&:last-child": {
    borderTopRightRadius: "6px !important",
    borderBottomRightRadius: "6px !important",
  },

  "&:hover": {
    backgroundColor: "#FFD7EB",
  },
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: "#7352c7",
    color: "white",
  },
}));

const ToggleButtonGroupField = ({
  name,
  value,
  options,
  setFieldValue,
  outcomeExistsPath,
}) => {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(e, value) => {
        if (outcomeExistsPath) {
          setFieldValue(outcomeExistsPath, value === "yes" ? true : false);
        }
        if (value) {
          setFieldValue(name, value);
        } else {
          setFieldValue(name, "");
        }
      }}
      aria-label={name}
      className={clsx(styles.toggleButtonGroup, "togglePickerPPE")}
    >
      {options.map((option) => (
        <MuiToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.label}
          className={styles.toggleButton}
        >
          {option.label}
        </MuiToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const getCurrentDate = () => {
  const options = { day: "2-digit", month: "short", year: "numeric" };
  const date = new Date();
  return date.toLocaleDateString("en-US", options);
};

const NarcoticsForm = ({ isDashboard, setIsDashboard }) => {
  const CurrentDate = getCurrentDate();
  const loggedInUser = JSON.parse(localStorage.getItem("user-profile"));
  const { enqueueSnackbar } = useSnackbar();

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");

  const [currentPatientName, setcurrentPatientName] = useState("");
  const [currentPatientUnit, setcurrentPatientUnit] = useState("");
  const [currentPatientQty1, setcurrentPatientQty1] = useState("");
  const [currentPatientQty2, setcurrentPatientQty2] = useState("");
  const [currentPatientQty3, setcurrentPatientQty3] = useState("");
  const [currentPatientMedication, setcurrentPatientMedication] = useState("");
  const [currentPatientPrescriptionNo, setcurrentPatientPrescriptionNo] =
    useState("");

  const resetCurrentPatientData = () => {
    setcurrentPatientName("");
    setcurrentPatientUnit("");
    setcurrentPatientQty1("");
    setcurrentPatientQty2("");
    setcurrentPatientQty3("");
    setcurrentPatientMedication("");
    setcurrentPatientPrescriptionNo("");
  };

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await getFacilities();
        if (response.data.status) {
          setFacilities(response.data.body);
          setSelectedFacility(response.data.body[0].name);
        }
      } catch (error) {
        console.error("Error fetching facilities: ", error);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    const selectedFacilityData = facilities.find(
      (facility) => facility.name === selectedFacility
    );

    setUnits(selectedFacilityData ? selectedFacilityData.units : []);
  }, [selectedFacility, facilities]);

  const getInitialValues = () => ({
    facility: selectedFacility,
    unit: selectedUnit,
    facilityDesignateName: "",
    pharmacyDesignateName: "",
    completedBy: loggedInUser ? loggedInUser.userName : "",
    qualitative: {
      q1: {
        answer: "",
        comment: "",
      },
      q2: {
        answer: "",
        comment: "",
      },
      q3: {
        answer: "",
        comment: "",
      },
      q4: {
        answer: "",
        comment: "",
      },
      q5: {
        answer: "",
        comment: "",
      },
      q6: {
        answer: "",
        comment: "",
      },
      q7: {
        answer: "",
        comment: "",
      },
      q8: {
        answer: "",
        comment: "",
      },
      q9: {
        answer: "",
        comment: "",
      },
      q10: {
        answer: "",
        comment: "",
      },
      q11: {
        answer: "",
        comment: "",
      },
      q12: {
        answer: "",
        comment: "",
      },
      q13: {
        answer: "",
        comment: "",
      },
      q14: {
        answer: "",
        comment: "",
      },
      q15: {
        answer: "",
        comment: "",
      },
      compliance: {
        percentage: 0,
        outcome: false,
      },
    },
    quantitative: {
      quantitativeCompliance: {
        totalRecords: 0,
        passedRecords: 0,
      },
      records: [
        // {
        //   residentName: "",
        //   unit: "",
        //   prescriptionNo: "",
        //   medication: "",
        //   quantity1: "",
        //   quantity2: "",
        //   quantity3: "",
        //   outcome: false,
        // },
      ],
    },
  });

  useEffect(() => {
    if (selectedFacility != "") {
      getInitialValues();
    }
  }, [selectedFacility, selectedUnit, getInitialValues]);

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const errorText = (error) => {
    try {
      return (
        <div
          style={{
            color: "red",
            fontSize: "0.7rem",
            textAlign: "left",
            visibility: error ? "visible" : "hidden",
            marginTop: "5px",
            // position: "absolute",
            // bottom: "7px",
            // left: "25px",
          }}
        >
          {error}
        </div>
      );
    } catch (e) {
      return "";
    }
  };

  const hasErrors = (errors) => Object.keys(errors).length > 0;

  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    // calculating qualitative compliance before submitting
    const qualitative = values.qualitative;
    let metAnswers = 0;
    const totalQuestions = 15;

    for (let i = 1; i <= totalQuestions; i++) {
      const question = qualitative[`q${i}`];
      if (question.answer === "met") {
        metAnswers++;
      }
    }

    const ratio = metAnswers / totalQuestions;
    const outcome = ratio >= 0.8;

    values.qualitative.compliance.percentage = ratio.toFixed(3);
    values.qualitative.compliance.outcome = outcome;

    const requestData = {
      facility: values.facility,
      unit: values.unit,
      facilityDesignateName: values.facilityDesignateName,
      pharmacyDesignateName: values.pharmacyDesignateName,
      // completedBy: values.completedBy,
      qualitative: values.qualitative,
      quantitative: values.quantitative,
    };

    console.log("Request Data Before Submitting:\n", requestData);

    createRecordNarcotics(requestData)
      .then((response) => {
        enqueueSnackbar(`${response?.data?.message}`, {
          variant: "success",
        });
        setSubmitting(false);
        resetForm();
        console.log("Successful Response\n", response);
      })
      .catch((error) => {
        enqueueSnackbar(`${error}`, {
          variant: "error",
        });
        console.error("Error submitting form:", error);
        setSubmitting(false);
      });
  };

  return (
    <div className={styles.narcoticsForm}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={narcoticsValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue, errors, resetForm, isSubmitting }) => {
          function checkComplianceOutcome(q1, q2, q3) {
            const outcome = q1 === q2 && q2 === q3;
            if (outcome) {
              setFieldValue(
                "quantitative.quantitativeCompliance.passedRecords",
                values.quantitative.quantitativeCompliance.passedRecords + 1
              );
            }

            return outcome;
          }

          return (
            <Form>
              <Paper className={styles.formPaper}>
                <div className={styles.narcoticsFormHeader}>
                  <h2>Narcotics Audit</h2>
                  <h2>{CurrentDate}</h2>
                </div>
                <div className={styles.narcoticsFormSectionBody}>
                  <Grid
                    container
                    alignItems="flex-start"
                    justifyContent="space-between"
                    // spacing={10}
                    style={{ marginBottom: "-20px" }}
                  >
                    <Grid item style={{ width: "30%" }}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="facility"
                          className={styles.narcoticsFormLabel}
                        >
                          Facility
                        </label>

                        <Field
                          as={Select}
                          name="facility"
                          variant="outlined"
                          value={values.facility}
                          onChange={(e) => {
                            setSelectedFacility(e.target.value);
                            setFieldValue("facility", e.target.value);
                          }}
                          fullWidth
                        >
                          {facilities.map((facility) => (
                            <MenuItem key={facility.name} value={facility.name}>
                              {facility.name}
                            </MenuItem>
                          ))}
                        </Field>
                        {errorText(errors?.facility)}
                      </div>
                    </Grid>
                    <Grid item style={{ width: "30%" }}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="unit"
                          className={styles.narcoticsFormLabel}
                        >
                          Unit
                        </label>
                        <Field
                          as={Select}
                          name="unit"
                          variant="outlined"
                          value={values.unit}
                          onChange={(e) => {
                            setSelectedUnit(e.target.value);
                            setFieldValue("unit", e.target.value);
                          }}
                          fullWidth
                        >
                          {units.map((unit) => (
                            <MenuItem key={unit.id} value={unit.name}>
                              {unit.name}
                            </MenuItem>
                          ))}
                        </Field>
                        {errorText(errors?.unit)}
                      </div>
                    </Grid>
                    <Grid item style={{ width: "30%" }}></Grid>
                  </Grid>
                  <Grid
                    container
                    alignItems="flex-start"
                    justifyContent="space-between"
                    style={{ marginTop: "30px" }}
                  >
                    <Grid item style={{ width: "30%" }}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="facilityDesignateName"
                          className={styles.narcoticsFormLabel}
                        >
                          Facility Designate Name
                        </label>

                        <TextField
                          name="facilityDesignateName"
                          value={values.facilityDesignateName}
                          onChange={(e) => {
                            setFieldValue(
                              "facilityDesignateName",
                              e.target.value
                            );
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      </div>
                      {errorText(errors?.facilityDesignateName)}
                    </Grid>
                    <Grid item style={{ width: "30%" }}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="pharmacyDesignateName"
                          className={styles.narcoticsFormLabel}
                        >
                          Pharmacy Designate Name
                        </label>

                        <TextField
                          name="pharmacyDesignateName"
                          value={values.pharmacyDesignateName}
                          onChange={(e) => {
                            setFieldValue(
                              "pharmacyDesignateName",
                              e.target.value
                            );
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      </div>
                      {errorText(errors?.pharmacyDesignateName)}
                    </Grid>
                    <Grid item style={{ width: "30%" }}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="completedBy"
                          className={styles.narcoticsFormLabel}
                        >
                          Completed By
                        </label>

                        <TextField
                          name="completedBy"
                          value={values.completedBy}
                          disabled
                          variant="outlined"
                          fullWidth
                        />
                      </div>
                      {errorText(errors?.completedBy)}
                    </Grid>
                  </Grid>
                </div>
              </Paper>
              {/* add patients info */}
              <Paper className={styles.formPaper}>
                <FieldArray
                  name="quantitative.records"
                  render={({ push }) => (
                    <div className={styles.narcoticsFormSectionBody}>
                      <Grid
                        container
                        justifyContent="space-between"
                        alignContent="stretch"
                        alignItems="flex-start"
                      >
                        <Grid item style={{ width: "25%" }}>
                          <div className={styles.singleFormItem}>
                            <label className={styles.narcoticsFormLabel}>
                              Patient Name
                            </label>
                            <TextField
                              value={currentPatientName}
                              onChange={(e) =>
                                setcurrentPatientName(e.target.value)
                              }
                              variant="outlined"
                              fullWidth
                              margin="normal"
                            />
                          </div>
                        </Grid>
                        <Grid
                          item
                          style={{ width: "16.66%" }}
                          className={styles.dropdownMoveUp}
                        >
                          <div className={styles.singleFormItem}>
                            <label className={styles.narcoticsFormLabel}>
                              Unit
                            </label>
                            <Field
                              as={Select}
                              variant="outlined"
                              value={currentPatientUnit}
                              onChange={(e) =>
                                setcurrentPatientUnit(e.target.value)
                              }
                              fullWidth
                            >
                              {units.map((unit) => (
                                <MenuItem key={unit.id} value={unit.name}>
                                  {unit.name}
                                </MenuItem>
                              ))}
                            </Field>
                          </div>
                        </Grid>
                        <Grid item style={{ width: "16.66%" }}>
                          <div className={styles.singleFormItem}>
                            <label className={styles.narcoticsFormLabel}>
                              Qty (Count Book)
                            </label>
                            <TextField
                              type="number"
                              value={currentPatientQty1}
                              onChange={(e) =>
                                setcurrentPatientQty1(e.target.value)
                              }
                              variant="outlined"
                              fullWidth
                              margin="normal"
                            />
                          </div>
                          {currentPatientQty1 < 0 && (
                            <div
                              style={{
                                color: "red",
                                fontSize: "0.7rem",
                                textAlign: "left",
                              }}
                            >
                              This can't be negative
                            </div>
                          )}
                        </Grid>
                        <Grid item style={{ width: "16.66%" }}>
                          <div className={styles.singleFormItem}>
                            <label className={styles.narcoticsFormLabel}>
                              Qty (Tracking Sheets)
                            </label>
                            <TextField
                              type="number"
                              value={currentPatientQty2}
                              onChange={(e) =>
                                setcurrentPatientQty2(e.target.value)
                              }
                              variant="outlined"
                              fullWidth
                              margin="normal"
                            />
                          </div>
                          {currentPatientQty2 < 0 && (
                            <div
                              style={{
                                color: "red",
                                fontSize: "0.7rem",
                                textAlign: "left",
                              }}
                            >
                              This can't be negative
                            </div>
                          )}
                        </Grid>
                        <Grid item style={{ width: "16.66%" }}>
                          <div className={styles.singleFormItem}>
                            <label className={styles.narcoticsFormLabel}>
                              Qty (Physical Count)
                            </label>
                            <TextField
                              type="number"
                              value={currentPatientQty3}
                              onChange={(e) =>
                                setcurrentPatientQty3(e.target.value)
                              }
                              variant="outlined"
                              fullWidth
                              margin="normal"
                            />
                          </div>
                          {currentPatientQty3 < 0 && (
                            <div
                              style={{
                                color: "red",
                                fontSize: "0.7rem",
                                textAlign: "left",
                              }}
                            >
                              This can't be negative
                            </div>
                          )}
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        alignItems="flex-end"
                        justifyContent="space-between"
                        className={styles.gridRowNarcoticsForm}
                        style={{
                          marginTop: "12px",
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <Grid
                          item
                          style={{ width: "25%", marginRight: "2.07%" }}
                        >
                          <div className={styles.singleFormItem}>
                            <label className={styles.narcoticsFormLabel}>
                              Medication Name
                            </label>
                            <TextField
                              value={currentPatientMedication}
                              onChange={(e) =>
                                setcurrentPatientMedication(e.target.value)
                              }
                              variant="outlined"
                              fullWidth
                              margin="normal"
                            />
                          </div>
                        </Grid>
                        <Grid item style={{ width: "16.66%" }}>
                          <div className={styles.singleFormItem}>
                            <label className={styles.narcoticsFormLabel}>
                              Prescription No.
                            </label>
                            <TextField
                              type="number"
                              value={currentPatientPrescriptionNo}
                              onChange={(e) =>
                                setcurrentPatientPrescriptionNo(e.target.value)
                              }
                              variant="outlined"
                              fullWidth
                              margin="normal"
                            />
                          </div>
                        </Grid>
                        <div className={styles.narcoticsPatientButtons}>
                          <Button
                            className={styles.redButton}
                            onClick={() => resetCurrentPatientData()}
                          >
                            Reset
                          </Button>
                          <Button
                            disabled={
                              currentPatientName === "" ||
                              currentPatientUnit === "" ||
                              currentPatientQty1 === "" ||
                              currentPatientQty2 === "" ||
                              currentPatientQty3 === "" ||
                              currentPatientMedication === "" ||
                              currentPatientPrescriptionNo === "" ||
                              currentPatientQty1 < 0 ||
                              currentPatientQty2 < 0 ||
                              currentPatientQty3 < 0
                            }
                            className={styles.primaryButton}
                            onClick={() => {
                              push({
                                residentName: currentPatientName,
                                unit: currentPatientUnit,
                                prescriptionNo: currentPatientPrescriptionNo,
                                medication: currentPatientMedication,
                                quantity1: currentPatientQty1,
                                quantity2: currentPatientQty2,
                                quantity3: currentPatientQty3,
                                outcome: checkComplianceOutcome(
                                  currentPatientQty1,
                                  currentPatientQty2,
                                  currentPatientQty3
                                ),
                              });
                              resetCurrentPatientData();
                              setFieldValue(
                                "quantitative.quantitativeCompliance.totalRecords",
                                values.quantitative.quantitativeCompliance
                                  .totalRecords + 1
                              );
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      </Grid>
                      {/* {errorText(errors?.quantitative?.records)} */}
                    </div>
                  )}
                />
              </Paper>
              {/* display patients */}
              <Paper className={styles.formPaper}>
                <div className={`${styles.narcoticsPatientsSectionHeader}`}>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item style={{ width: "5%" }}></Grid>
                    <Grid item style={{ width: "12.85%" }}>
                      <p className={styles.narcoticsPatientNameHeader}>
                        Patient Name
                      </p>
                    </Grid>
                    <Grid item style={{ width: "12.85%" }}>
                      <p>Floor / Unit</p>
                    </Grid>
                    <Grid item style={{ width: "12.85%" }}>
                      <p>Prescription No</p>
                    </Grid>
                    <Grid item style={{ width: "12.85%" }}>
                      <p>Medication</p>
                    </Grid>
                    <Grid item style={{ width: "12.85%" }}>
                      <p>Qty (Count Book)</p>
                    </Grid>
                    <Grid item style={{ width: "12.85%" }}>
                      <p>Qty (Tracking)</p>
                    </Grid>
                    <Grid item style={{ width: "12.85%" }}>
                      <p>Qty (Physical)</p>
                    </Grid>
                    <Grid item style={{ width: "5%" }}></Grid>
                  </Grid>
                </div>
                <div className={styles.narcoticsPatientsList}>
                  <FieldArray
                    name="quantitative.records"
                    render={({ remove }) => (
                      <div>
                        {values?.quantitative?.records?.map(
                          (patient, index) =>
                            (patient.residentName !== "" ||
                              patient.unit !== "" ||
                              patient.quantity1 !== "" ||
                              patient.quantity2 !== "" ||
                              patient.quantity3 !== "" ||
                              patient.medication !== "" ||
                              patient.prescriptionNo !== "") && (
                              <div
                                key={index}
                                className={styles.singleNarcoticsPatient}
                              >
                                <Grid
                                  container
                                  alignItems="center"
                                  direction="row"
                                >
                                  <Grid item style={{ width: "5%" }}>
                                    <IconButton
                                      type="button"
                                      aria-label="delete"
                                      className={
                                        styles.narcoticsPatientDeleteBtn
                                      }
                                      onClick={() => {
                                        remove(index);
                                        setFieldValue(
                                          "quantitative.quantitativeCompliance.totalRecords",
                                          values.quantitative
                                            .quantitativeCompliance
                                            .totalRecords - 1
                                        );
                                        if (patient.outcome) {
                                          setFieldValue(
                                            "quantitative.quantitativeCompliance.passedRecords",
                                            values.quantitative
                                              .quantitativeCompliance
                                              .passedRecords - 1
                                          );
                                        }
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Grid>

                                  <Grid item style={{ width: "12.85%" }}>
                                    <p className={styles.narcoticsPatientName}>
                                      {patient.residentName}
                                    </p>
                                  </Grid>
                                  <Grid item style={{ width: "12.85%" }}>
                                    <p>{patient.unit}</p>
                                  </Grid>
                                  <Grid item style={{ width: "12.85%" }}>
                                    <p>{patient.prescriptionNo}</p>
                                  </Grid>
                                  <Grid item style={{ width: "12.85%" }}>
                                    <p>{patient.medication}</p>
                                  </Grid>
                                  <Grid item style={{ width: "12.85%" }}>
                                    <p>{patient.quantity1}</p>
                                  </Grid>
                                  <Grid item style={{ width: "12.85%" }}>
                                    <p>{patient.quantity2}</p>
                                  </Grid>
                                  <Grid item style={{ width: "12.85%" }}>
                                    <p>{patient.quantity3}</p>
                                  </Grid>
                                  <Grid
                                    item
                                    alignContent="center"
                                    style={{ textAlign: "center", width: "5%" }}
                                  >
                                    {patient.outcome ? (
                                      <CheckCircleIcon
                                        style={{ color: "#4CAF50" }}
                                      />
                                    ) : (
                                      <CancelOutlinedIcon
                                        style={{ color: "red" }}
                                      />
                                    )}
                                  </Grid>
                                </Grid>
                              </div>
                            )
                        )}
                        {values.quantitative.records.length === 0 && (
                          <Typography
                            variant="body2"
                            className={styles.observationPointMargin}
                            style={{ color: "#ff0000", fontWeight: "bold" }}
                          >
                            No Patient Data Added
                          </Typography>
                        )}
                      </div>
                    )}
                  />
                </div>
              </Paper>
              {/* qualitative questions start here */}
              {NarcoticsQualitativeQuestions.map((question, index) => (
                <Paper key={index} className={styles.formPaper}>
                  <div className={styles.narcoticsQualitativeFormSectionBody}>
                    <div className={styles.narcoticsQualitativeField}>
                      <Grid container spacing={4}>
                        <Grid item sm={6}>
                          <Typography
                            variant="body2"
                            className={styles.observationPointMargin}
                          >
                            {question}
                          </Typography>
                          <ToggleButtonGroupField
                            name={`qualitative.q${index + 1}.answer`}
                            value={values.qualitative[`q${index + 1}`].answer}
                            options={toggleOptions}
                            setFieldValue={setFieldValue}
                            className={styles.observationPointMargin}
                          />
                          {errorText(
                            errors?.qualitative?.[`q${index + 1}`]?.answer
                          )}
                        </Grid>
                        <Grid item sm={6} style={{ alignContent: "center" }}>
                          <div className={styles.narcoticsFormCommentField}>
                            <TextField
                              name={`qualitative.q${index + 1}.comment`}
                              value={
                                values.qualitative[`q${index + 1}`].comment
                              }
                              onChange={(e) =>
                                setFieldValue(
                                  `qualitative.q${index + 1}.comment`,
                                  e.target.value
                                )
                              }
                              label="Comments"
                              variant="outlined"
                              fullWidth
                              multiline
                              // minRows={3}
                              maxRows={4}
                              InputProps={{
                                rows: 3,
                                maxRows: 4,
                                multiline: true,
                                inputComponent: "textarea",
                              }}
                              // margin="normal"
                              classes={{
                                root: "narcoticsCommentBoxFieldRoot",
                              }}
                              className={"narcoticsCommentBoxField"}
                            />
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  </div>
                </Paper>
              ))}
              {/* form finalize button */}
              <div className={styles.narcoticsFormFinalizeBtns}>
                <Button
                  type="reset"
                  variant="outline"
                  className={styles.redButton}
                  onClick={() => {
                    resetForm();
                    setSelectedFacility("");
                  }}
                  style={{ marginLeft: "auto" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={styles.primaryButton}
                  disabled={isSubmitting || hasErrors(errors && errors)}
                >
                  <ButtonTextLoading
                    isSubmitting={isSubmitting}
                    body={"SUBMIT"}
                  />
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NarcoticsForm;
